import _ from "lodash";
import moment from "moment-timezone";
import React from "react";

import { Button2 } from "@/ui2/atoms/Button";
import { Card2 } from "@/ui2/atoms/Card";
import { CardHeading } from "@/components/UI/Headings";
import { DIAGNOSTIC_INTERVIEW_90791 } from "@/components/Provider/ScheduleAvailability/constants";
import { Divider2 } from "@/ui2/atoms/Divider";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { Heading2 } from "@/ui2/atoms/Heading";
import images from "@/utils/images";
import { Modal2 } from "@/ui2/molecules/Modal";
import { toUnixTimestamp } from "@/lib/helpers/toUnixTimestamp";
import { ViewTitle } from "@/features/dashboard/components/ViewTitle";

import { usePatientAccomplishments } from "../../hooks/usePatientAccomplishments";

export function AccomplishmentsWidget({ profile }) {
  const { memberMilestoneActivities = [], memberSessionActivities = [] } =
    usePatientAccomplishments(profile) ?? {};

  const renderActivityIcon = (icon) => {
    return <img alt="" className="img-fluid my-0 mb-4 mb-md-0 mx-0 mx-md-2" src={icon} />;
  };

  const renderActivitySectionBody = (activity, index, activityList) => {
    const { content, header, icon } = ACTIVITY_CONTENT_TRANSFORM_MAP[activity.type]
      ? ACTIVITY_CONTENT_TRANSFORM_MAP[activity.type](activity)
      : {};

    let rowClasses = "";

    if (activityList.length > 1) {
      rowClasses = "ActivitySectionBodyItem";
      if (index === 0) {
        rowClasses = "ActivitySectionBodyFirstItem";
      } else if (index === activityList.length - 1) {
        rowClasses = "ActivitySectionBodyLastItem tw:[&>div]:pb-0!";
      }
    }

    return header && content && icon ? (
      <li className={`row ${rowClasses}`} key={`activity-${index}`}>
        <div className="col d-flex align-items-center pb-3">
          {renderActivityIcon(icon)}

          <div>
            <CardHeading className="d-lg-inline-block pl-1 fs-16 mb-0" text={header} />
            <p className="fs-14 pl-1 my-0 mb-4 mb-md-0 txt-gry" data-public>
              {content}
            </p>
          </div>
        </div>
      </li>
    ) : (
      <></>
    );
  };

  const renderActivitySection = (sectionTitle, activities, activityAmount = 2) => {
    const clonedActivities = _.cloneDeep(activities);
    const activitiesToRender = clonedActivities.slice(0, activityAmount);

    return activitiesToRender ? (
      <>
        <header className="tw:flex tw:items-center tw:justify-between">
          <Heading2 as="h3" className="tw:mt-0! tw:font-normal!" size={1}>
            {sectionTitle}
          </Heading2>

          <Modal2
            title={sectionTitle}
            trigger={
              <Button2 className="tw:p-1" variant="link">
                View all
              </Button2>
            }
          >
            <ul className="tw:max-h-[50vh] tw:overflow-x-hidden tw:overflow-y-auto tw:pr-4">
              {clonedActivities.map(renderActivitySectionBody)}
            </ul>
          </Modal2>
        </header>

        <Divider2 className="tw:-mt-2!" />

        <ul>{activitiesToRender.map(renderActivitySectionBody)}</ul>
      </>
    ) : (
      <></>
    );
  };

  const renderedSections = {
    milestoneActivities: memberMilestoneActivities,
    sessionActivities: memberSessionActivities,
  };

  if (memberMilestoneActivities.length > 0) {
    renderedSections["milestoneActivities"] = renderActivitySection(
      "Milestones",
      memberMilestoneActivities,
    );
  }

  if (memberSessionActivities.length > 0) {
    const uniqueSessionActivities = [
      ...new Map(
        memberSessionActivities.map((activity) => [
          `${activity["timestamp"]}|${activity["title"]}|${activity["type"]}`,
          activity,
        ]),
      ).values(),
    ];

    renderedSections["sessionActivities"] = renderActivitySection(
      "Completed Sessions",
      uniqueSessionActivities,
    );
  }

  return (
    <div
      className="tw:grid tw:grid-rows-[min-content_1fr] tw:gap-4"
      {...getAutomationDataAttr("patient-home-accomplishments-widget")}
    >
      <ViewTitle as="h2" size={3}>
        Accomplishments
      </ViewTitle>

      <Card2 className="tw:flex tw:flex-col tw:gap-4 tw:p-5">
        {renderedSections["milestoneActivities"]}
        {renderedSections["sessionActivities"]}
      </Card2>
    </div>
  );
}

const buildScheduleCardTitle = (activity) => {
  return {
    content: `${activity["timestamp"]}`,
    header: activity["title"],
    icon: images("./icons/common/milestone/extended_care_started.svg"),
  };
};

const buildMilestoneCardTitle = (activity) => {
  const callDateTime = moment.unix(activity["timestamp"]).utc().local().format("LLL");

  return { content: `${callDateTime}`, header: activity["title"], icon: activity["icon"] };
};

const ACTIVITY_CONTENT_TRANSFORM_MAP = {
  milestone: buildMilestoneCardTitle,
  schedule: buildScheduleCardTitle,
};

export const buildPatientAccomplishmentSessions = (activities) => {
  return activities
    .filter((activity) => activity.key_content["appointment_status"] === "Appointment Completed")
    .map((activity) => {
      const callDateTime = moment
        .utc(`${activity.key_content["call_date"]} ${activity.key_content["call_time"]}`)
        .local()
        .format("LLL");
      const allottedTime = activity.key_content["duration"]
        ? ` - ${activity.key_content["duration"]} minutes`
        : "";
      const type = "schedule";
      const timestamp = `${callDateTime + allottedTime}`;
      const providerName = activity.key_content["provider_name"]
        ? ` with ${activity.key_content["provider_name"]}`
        : "";

      if (activity.key_content["call_event_type"] === DIAGNOSTIC_INTERVIEW_90791) {
        return {
          icon: `${images("./icons/common/intake_bhs/intake-bhs-sec-white.svg")}`,
          timestamp,
          title: "Therapist Consultation" + providerName,
          type,
        };
      } else if (activity.key_content["provider_type"] === "PRESCRIBE") {
        return {
          icon: `${images("./icons/common/prescriber/presciber-sec-white.svg")}`,
          timestamp,
          title: "Medication Management Session" + providerName,
          type,
        };
      } else {
        return {
          icon: `${images("./icons/common/therapist/therapist-sec-white.svg")}`,
          timestamp,
          title: "Therapist Session" + providerName,
          type,
        };
      }
    });
};

export const buildPatientAccomplishmentMilestones = (patient) => {
  const type = "milestone";
  const milestoneEventProps = [
    "ninety_day_start_date",
    "ninety_day_30_days_completed_date_time",
    "ninety_day_60_days_completed_date_time",
    "ninety_day_90_days_completed_date_time",
    "assessment_completed_date",
    "initial_video_consult_date_time",
    "extended_care_start_date_time",
    "created_date",
    "initial_prescriber_call_date_time",
  ];

  const milestoneEventHeaderMap = {
    assessment_completed_date: {
      icon: `${images("./icons/common/milestone/assessment_complete.svg")}`,
      title: "Assessment Completed",
    },
    created_date: {
      icon: `${images("./icons/common/small_logo/logo-sec-white.svg")}`,
      title: "Became an aptihealth Member",
    },
    extended_care_start_date_time: {
      icon: `${images("./icons/common/milestone/extended_care_started.svg")}`,
      title: "Extended Care Program Started",
    },
    initial_prescriber_call_date_time: {
      icon: `${images("./icons/common/milestone/first_session.svg")}`,
      title: "First Session Completed",
    },
    initial_video_consult_date_time: {
      icon: `${images("./icons/common/milestone/first_session.svg")}`,
      title: "First Session Completed",
    },
    ninety_day_30_days_completed_date_time: {
      icon: `${images("./icons/common/milestone/30_day.svg")}`,
      title: "30 Days on aptihealth",
    },
    ninety_day_60_days_completed_date_time: {
      icon: `${images("./icons/common/milestone/60_day.svg")}`,
      title: "60 Days on aptihealth",
    },
    ninety_day_90_days_completed_date_time: {
      icon: `${images("./icons/common/milestone/90_day_completed.svg")}`,
      title: "90-Day Care Program Completed",
    },
    ninety_day_start_date: {
      icon: `${images("./icons/common/milestone/90_day_started.svg")}`,
      title: "90-Day Care Started",
    },
  };

  const milestones = [];

  milestoneEventProps.forEach((property) => {
    if (patient[property]) {
      const timestamp = toUnixTimestamp(patient[property]);

      if (timestamp === null) return;

      // quick fix for teens + kids, if they're auto flagged to have passed the assessment, stash it from the users sight
      if (
        property === "assessment_completed_date" &&
        patient[property] === patient["assessment_started_date"] &&
        patient.group_type === "teens_kids_program"
      ) {
        return;
      }
      const activity = { timestamp, type, ...milestoneEventHeaderMap[property] };
      let existingMilestone = milestones.find(
        (milestone) => milestone["title"] === activity["title"],
      );

      if (existingMilestone) {
        existingMilestone =
          activity["timestamp"] < existingMilestone["timestamp"] ? activity : existingMilestone;
      } else {
        milestones.push(activity);
      }
    }
  });

  return milestones.sort((activityA, activityB) => activityB["timestamp"] - activityA["timestamp"]);
};
